import React, { useRef, useMemo, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { MessageSquare, BarChart3, Layers, TrendingUp, BookOpen, Sparkles, Zap, Target } from 'lucide-react';
import LandingStyleHero from '../components/productHero/LandingStyleHero';
import ProductDemoVideoEmbed from '../components/video/ProductDemoVideoEmbed';
import { getWalkthroughVideo } from '../config/walkthroughVideos';
import SEO from '../components/SEO';
import { PRODUCTION_PATHS, resolveHostedEmbedUrl } from '../config/siteUrl';
import { LoadingSpinner } from '../components/LoadingSpinner';
import PremiumAccessPrompt from '../components/PremiumAccessPrompt';
import { usePremiumProductAccess } from '../hooks/usePremiumProductAccess';
import { PREMIUM_PRODUCT_KEYS } from '../config/premiumProducts';

const sectionTitleClass =
  'text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight bg-gradient-to-r from-slate-900 via-slate-700 to-slate-900 bg-clip-text text-transparent';

const glassCardClass =
  'rounded-2xl border border-slate-200/80 bg-white/95 shadow-lg shadow-slate-200/50 p-5 sm:p-6 md:p-8 transition-all duration-300 hover:shadow-xl hover:border-sky-200/60 hover:-translate-y-0.5';

const whatYouBuild = [
  { icon: Layers, title: 'Cleaner structure', text: 'Practice breaking messy business problems into clear, usable branches instead of defaulting to generic frameworks.' },
  { icon: Target, title: 'Better synthesis under pressure', text: 'Train yourself to land the answer, name the risk, and move the case forward when the pressure rises.' },
  { icon: BarChart3, title: 'A repeatable feedback loop', text: 'See where your answer held up, where it drifted, and what to tighten before your next case.' },
];

const howItWorks = [
  { icon: BookOpen, title: 'Pick a McKinsey-style case', text: 'Choose from cases built for interviewer-led practice so you can sharpen how you structure, respond, and move through a real McKinsey-style interview.' },
  { icon: MessageSquare, title: 'Run the interview live', text: 'The interviewer pushes one strong question at a time so you can think clearly, ask for what you need, and stay disciplined.' },
  { icon: BarChart3, title: 'Get structured feedback', text: 'Review the parts of your performance that matter most in a live case: structure, math, synthesis, and decision quality.' },
  { icon: TrendingUp, title: 'See your progress stack up', text: 'Use the dashboard to spot patterns across sessions and decide what to focus on before your next real round.' },
];

const whatMakesMckinsey = [
  { icon: Target, title: 'Interviewer-led and hypothesis-first', text: 'The interviewer controls the cadence, but you still need to respond with clear structure, strong hypotheses, and disciplined thinking under pressure.' },
  { icon: Layers, title: 'Disciplined stage flow', text: 'The practice moves in a realistic order so you do not jump to data too early or synthesize before the case is ready for it.' },
  { icon: Zap, title: 'Feedback you can act on', text: 'The point is not just to score the turn. The point is to help you know exactly what to improve on the next rep.' },
];

function getMckCoachLaunchUrl() {
  return resolveHostedEmbedUrl({
    envName: 'REACT_APP_MCKINSEY_AI_COACH_DEMO_URL',
    localhostUrl: 'http://localhost:5174',
    productionPath: PRODUCTION_PATHS.mckinseyAICoach,
  });
}

const relatedLinks = [
  { label: 'Sea Wolf Simulation', path: '/seawolf-practice', Icon: Zap },
  { label: 'Red Rock cases', path: '/redrock', Icon: BookOpen },
  { label: 'About McKinsey Solve', path: '/about-solve', Icon: Sparkles },
];

/** Match WhyCognidoorV4 carousel timing / easing for McKinsey hero preview. */
const MCK_PREVIEW_CAROUSEL_INTERVAL_MS = 6000;
const MCK_PREVIEW_SLIDE_DURATION_MS = 500;

const mckCoachPreviewSlides = [
  {
    src: '/images/landing/ai-cases-mckinsey.png',
    alt: 'McKinsey AI Coach — case library and feedback',
    badge: 'Case library',
  },
  {
    src: '/images/landing/ai-cases-mckinsey-war-room.png',
    alt: 'McKinsey AI Coach — live session with interviewer and coach feedback',
    badge: 'Live session',
  },
];

function McKinseyCoachPreviewPanel() {
  const [slideIndex, setSlideIndex] = useState(0);
  const prevSlideRef = useRef(0);
  const [noTransition, setNoTransition] = useState(false);

  useEffect(() => {
    const id = setInterval(() => {
      setSlideIndex((i) => (i + 1) % mckCoachPreviewSlides.length);
    }, MCK_PREVIEW_CAROUSEL_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (slideIndex === 0 && prevSlideRef.current === mckCoachPreviewSlides.length - 1) {
      setNoTransition(true);
      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => setNoTransition(false));
      });
      return () => cancelAnimationFrame(raf);
    }
    prevSlideRef.current = slideIndex;
  }, [slideIndex]);

  const slide = mckCoachPreviewSlides[slideIndex];

  return (
    <>
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(2,6,23,0.06)_0%,rgba(12,34,66,0.12)_100%)]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sky-300/60 to-transparent" />
      <div className="relative border-b border-sky-400/20 p-4 text-white bg-slate-900/40">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-bold text-sky-200/90">McKinsey Lilli AI Coach</span>
          <span className="rounded-full bg-sky-500/20 px-2.5 py-1 text-[11px] font-semibold text-sky-200 transition-opacity duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]">
            {slide.badge}
          </span>
        </div>
      </div>
      <div className="relative p-4">
        <div className="rounded-2xl border border-white/15 overflow-hidden bg-white/5 backdrop-blur-sm ring-1 ring-white/5 shadow-[0_16px_36px_-24px_rgba(2,6,23,0.78)]">
          <div className="relative w-full overflow-hidden">
            <div
              className="flex w-[200%] ease-[cubic-bezier(0.32,0.72,0,1)]"
              style={{
                transform: `translateX(-${slideIndex * 50}%)`,
                transition: noTransition
                  ? 'none'
                  : `transform ${MCK_PREVIEW_SLIDE_DURATION_MS}ms cubic-bezier(0.32,0.72,0,1)`,
              }}
            >
              {mckCoachPreviewSlides.map((s, i) => (
                <div key={s.src} className="w-1/2 shrink-0 min-w-[50%] max-w-[50%]">
                  <img src={s.src} alt={s.alt} className="w-full h-auto block" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default function McKinseyLilliAICoachPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const coachLaunchUrl = useMemo(getMckCoachLaunchUrl, []);
  const coachAccess = usePremiumProductAccess(PREMIUM_PRODUCT_KEYS.mckinseyAICoach);
  const walkthroughVideo = getWalkthroughVideo('mckAiCoach');

  const handleViewPricing = () => navigate('/', { state: { scrollToPricing: true } });
  const handleSeeDemo = () => navigate('/', { state: { scrollToLiveDemos: true, track: 'mck', demoVariantMck: 'aiCoach' } });
  const handleScrollToWalkthrough = () => document.getElementById('mckinsey-proof')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const handleOpenCoach = () => {
    if (coachAccess.isLoading) return;
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    coachAccess.attemptAccess(
      () => {
        window.open(coachLaunchUrl, '_blank', 'noopener,noreferrer');
      },
      {
        returnTo,
        onDemo: handleSeeDemo,
      }
    );
  };
  const scrollToCaseLibrary = () => {
    const el = document.getElementById('mckinsey-proof');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <SEO
        title="McKinsey Lilli AI Interview Coach | Cognidoor"
        description="Practice McKinsey-style interviewer-led case interviews with an AI coach. Build MECE structure, sharpen synthesis, and prepare for both classic interviews and the McKinsey Lilli AI case assessment."
        url="/mckinsey-ai-coach"
      />
      <div className="bg-white text-gray-900 font-sans" style={{ '--cog-blue-1': '#277AB2', '--cog-blue-2': '#0B5996', '--cog-blue-3': '#082B4A' }}>
        <LandingStyleHero
          track="mck"
          headline="Practise live McKinsey-style cases with coaching that sharpens your structure and next step."
          subheadline="Run interviewer-led McKinsey-style case interviews with an AI interviewer that pushes your thinking live, then use the same coach to prepare for the McKinsey Lilli AI case assessment with stronger prompting, structure, and judgment."
          eyebrowChips={['Live AI-led practice', 'Hypothesis-first coaching', 'Progress dashboard']}
          proofRow={[
            { value: 'Live interviewer + coach' },
            { value: 'Skill-by-skill feedback' },
            { value: 'Track improvement over time' },
          ]}
          primaryCtaLabel="Open AI Coach"
          onPrimaryCta={scrollToCaseLibrary}
          secondaryCtaLabel="Watch walkthrough"
          onSecondaryCta={handleScrollToWalkthrough}
          previewChildren={<McKinseyCoachPreviewPanel />}
        />

        <section id="what-you-build" className="py-10 sm:py-14 bg-gradient-to-b from-white via-slate-50/50 to-white text-center px-4 sm:px-6 md:px-10">
          <h2 className={sectionTitleClass + ' mb-8 sm:mb-10'}>What gets stronger when you practise this way</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {whatYouBuild.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={glassCardClass + ' text-left'}
              >
                <item.icon className="w-8 h-8 text-sky-600 mb-3" strokeWidth={1.8} />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section id="mckinsey-proof" className="py-20 sm:py-28 bg-white px-4 sm:px-8 md:px-12 lg:px-16">
          <div className="max-w-[120rem] mx-auto">
            <h2 className={sectionTitleClass + ' text-center mb-8 sm:mb-10'}>Case library and feedback</h2>
            <p className="text-center text-gray-600 max-w-2xl mx-auto mb-12 sm:mb-14 text-sm sm:text-base">
              Choose from McKinsey-style cases built for interviewer-led practice. Run a case live with the AI coach and see exactly where your structure, math, and synthesis hold up, then use the same product to prepare for the McKinsey Lilli AI case assessment.
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-[1.25fr_0.9fr] gap-8 lg:gap-10 items-center">
              <div>
                <div className="rounded-2xl border border-slate-200/80 bg-white/95 shadow-xl overflow-hidden">
                <ProductDemoVideoEmbed videoKey="mckAiCoach" />
                <div className="px-4 py-2.5 border-t border-slate-100 bg-white">
                  <div className="text-sm font-semibold text-gray-900">{walkthroughVideo.title}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{walkthroughVideo.description}</div>
                </div>
                <div className="px-4 py-4 border-t border-slate-100 bg-white flex justify-center">
                  <button
                    type="button"
                    onClick={handleOpenCoach}
                    disabled={coachAccess.isLoading}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-base font-semibold rounded-xl text-white shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 bg-gradient-to-r from-sky-500 via-blue-600 to-blue-700 hover:from-sky-600 hover:via-blue-700 hover:to-blue-800"
                  >
                    {coachAccess.isLoading ? (
                      <>
                        <LoadingSpinner size="sm" tone="onPrimary" className="shrink-0" />
                        Checking access...
                      </>
                    ) : (
                      'Open AI Coach'
                    )}
                  </button>
                </div>
                </div>
              </div>
              <div className="space-y-3">
                {[
                  { label: 'Structure', text: 'See whether your branches made the analysis clear and usable.' },
                  { label: 'Math', text: 'See whether your numbers supported the recommendation.' },
                  { label: 'Synthesis', text: 'See whether you landed the answer clearly.' },
                  { label: 'Next move', text: 'Know exactly what to change on the next rep.' },
                ].map((item, i) => (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, y: 8 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.06 }}
                    className="flex gap-3 rounded-xl border border-slate-200/80 bg-white p-5 text-left"
                  >
                    <span className="text-sky-600 font-semibold text-base shrink-0">{item.label}:</span>
                    <span className="text-gray-600 text-base">{item.text}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 sm:py-14 bg-gradient-to-b from-white via-slate-50/50 to-white text-center px-4 sm:px-6 md:px-10">
          <h2 className={sectionTitleClass + ' mb-8 sm:mb-10'}>How it works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {howItWorks.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={glassCardClass + ' text-left'}
              >
                <item.icon className="w-8 h-8 text-sky-600 mb-3" strokeWidth={1.8} />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="py-10 sm:py-14 bg-white text-center px-4 sm:px-6 md:px-10">
          <h2 className={sectionTitleClass + ' mb-8 sm:mb-10'}>Why this feels closer to real McKinsey practice</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {whatMakesMckinsey.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={glassCardClass + ' text-left'}
              >
                <item.icon className="w-8 h-8 text-sky-600 mb-3" strokeWidth={1.8} />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="py-10 sm:py-14 bg-gradient-to-b from-white via-slate-50/30 to-white text-center px-4 sm:px-6 md:px-10">
          <h2 className={sectionTitleClass + ' mb-6 sm:mb-8'}>Build your McKinsey prep stack</h2>
          <p className="text-gray-600 max-w-2xl mx-auto mb-6 text-sm">
            Use the coach for live case practice. Add Sea Wolf and Red Rock when you want assessment-specific reps that sharpen your problem solving under pressure.
          </p>
          <div className="flex flex-wrap justify-center gap-4 max-w-3xl mx-auto">
            {relatedLinks.map(({ label, path, Icon }) => (
              <motion.button
                key={path}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate(path)}
                className="inline-flex items-center gap-2 rounded-xl border-2 border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-gray-700 shadow-sm hover:border-sky-300 hover:bg-sky-50/50 hover:text-sky-800 transition-colors"
              >
                <Icon className="w-4 h-4" strokeWidth={2} /> {label}
              </motion.button>
            ))}
          </div>
          <p className="mt-6 text-sm text-gray-500">
            <button type="button" onClick={handleViewPricing} className="font-semibold text-sky-600 hover:text-sky-700 underline underline-offset-2">
              See plans and pricing
            </button>
          </p>
        </section>
      </div>
      <PremiumAccessPrompt {...coachAccess.promptProps} />
    </>
  );
}
